import { useState, useEffect, useMemo } from "react";
import { useLocation, useSearch } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ArrowRight,
  Check,
  CreditCard,
  User,
  Building2,
  UserCircle2,
  Hash,
  Search,
  UserCheck,
  GraduationCap,
  Smartphone,
  Banknote,
  CalendarClock,
} from "lucide-react";
import { useCurrentUser } from "@/lib/auth";
import { useEleveSoldes } from "@/lib/backoffice-hooks";
import { formatXof } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Eyebrow } from "@/components/finances/primitives";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  OperateurLogo,
  OPERATEUR_META,
  type Operateur,
} from "@/components/payer/OperateurLogo";
import { HorsSystemeForm } from "@/components/payer/HorsSystemeForm";
import {
  ResteAReglerPicker,
  cleOption,
  optionChoisie,
  optionsDeReste,
} from "@/components/payer/ResteAReglerPicker";
import {
  getEcole,
  getMotifsForEcole,
  getResteARegler,
  initiatePaiementGuest,
  listMyEleves,
  searchElevesPublic,
  ApiError,
  type InitiatePaiementGuestBody,
  type EleveSearchRow,
  type Motif,
  type MyEleve,
} from "@/lib/api";
import { friendlyError } from "@/lib/errors";
import {
  firstPayerError,
  isValidEmail,
  isValidPhone,
  type PayerFormState,
} from "@/lib/payer-validation";

// La méthode envoyée au backend reste l'union historique (cf. lib/api). On
// récupère uniquement l'habillage visuel du proto /finances, pas le mock.
type Methode = Operateur;

// Ordre d'affichage des tuiles. Identique à la valeur attendue par le backend.
// CARTE_BANCAIRE est volontairement masquée pour le moment (désactivée côté UI ;
// l'enum backend reste inchangé). Réactiver en ré-ajoutant "CARTE_BANCAIRE" ici.
const METHODES: Methode[] = [
  "WAVE",
  "ORANGE_MONEY",
  "MTN_MOMO",
  "MOOV_MONEY",
  "DJAMO",
];

/** Projette un enfant lié (MyEleve) sur la forme EleveSearchRow utilisée par la
 *  sélection d'élève (carte « élève sélectionné » + payload matricule). */
function myEleveToRow(child: MyEleve): EleveSearchRow {
  return {
    id: child.id,
    nom: child.nom,
    prenoms: child.prenoms,
    matricule: child.matricule ?? "",
    classe: child.classeLibelle ?? child.classeId ?? null,
    dateNaissance: child.dateNaissance ?? null,
  };
}

function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState<T>(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

export default function Payer() {
  const [, setLocation] = useLocation();
  const searchString = useSearch();
  const searchParams = useMemo(
    () => new URLSearchParams(searchString),
    [searchString],
  );
  const ecoleIdParam = searchParams.get("ecoleId");
  const motifIdParam = searchParams.get("motifId");
  const eleveIdParam = searchParams.get("eleveId");

  const { isAuthenticated, user } = useCurrentUser();

  const [ecoleId] = useState<string | null>(ecoleIdParam);
  const [motifId, setMotifId] = useState<string | null>(motifIdParam);
  const [methode, setMethode] = useState<Methode | null>(null);
  // Onglet du mode de règlement : paiement Mobile Money en ligne (défaut) ou
  // déclaration d'un paiement déjà effectué hors système (virement/dépôt…).
  const [paiementMode, setPaiementMode] = useState<"online" | "offline">("online");

  const { data: ecole } = useQuery({
    queryKey: ["ecole", ecoleId],
    queryFn: () => getEcole(ecoleId!),
    enabled: !!ecoleId,
  });
  // Liste des enfants du parent connecté. Pour un parent AUTHENTIFIÉ on remplace
  // la recherche libre/globale d'élève par la sélection de SES enfants liés
  // (sécurité : un parent ne paie que pour ses propres enfants). On la charge
  // aussi en mode invité dès qu'un eleveId est présent (préremplissage dashboard).
  const { data: myEleves } = useQuery({
    queryKey: ["my-eleves"],
    queryFn: listMyEleves,
    enabled: isAuthenticated || !!eleveIdParam,
    retry: false,
  });

  // Identification enfant (invité) : le matricule EXACT. Le serveur ne renvoie
  // plus de liste par nom (21/10/2026, audit SCOM-01) : une seule ligne de
  // confirmation, prénom, initiale du nom et classe.
  const [eleveQuery, setEleveQuery] = useState("");
  const [selectedEleve, setSelectedEleve] = useState<EleveSearchRow | null>(null);

  // Tarifs PAR CLASSE : tant qu'aucun élève n'est choisi, le serveur ne peut
  // renvoyer que le forfait de l'école — qui ne vaut pas pour tous les niveaux.
  // Dès qu'un élève est sélectionné, sa classe est transmise et chaque motif
  // porte son `montantApplicableXof`. La classe fait partie de la queryKey pour
  // que le catalogue soit rechargé au changement d'enfant.
  const classePourTarif = selectedEleve?.classe ?? null;
  const { data: motifs } = useQuery({
    queryKey: ["ecole", ecoleId, "motifs", classePourTarif],
    queryFn: () => getMotifsForEcole(ecoleId!, classePourTarif),
    enabled: !!ecoleId,
  });

  const [elevePrefilled, setElevePrefilled] = useState(false);
  const [matricule, setMatricule] = useState("");

  const debouncedQuery = useDebounced(eleveQuery, 300);
  // La recherche libre n'est utilisée QUE pour les invités (non connectés). Un
  // parent connecté choisit parmi ses enfants liés → pas d'appel global.
  const { data: eleveResults, isFetching: searching, error: searchError } = useQuery({
    queryKey: ["eleves-search", ecoleId, debouncedQuery],
    queryFn: () => searchElevesPublic(ecoleId!, debouncedQuery),
    enabled:
      !isAuthenticated &&
      !!ecoleId &&
      debouncedQuery.trim().length >= 2 &&
      !selectedEleve,
  });
  // 21/10/2026 : le serveur limite les recherches par connexion (429). On le
  // dit, plutôt que « Aucun élève pour ce matricule », qui serait faux.
  const tropDeRecherches = searchError instanceof ApiError && searchError.status === 429;

  // Enfants du parent proposables sur cette page : filtrés par l'école ciblée
  // par le contexte de paiement (ecoleId), le cas échéant.
  const myChildrenForEcole = useMemo(() => {
    const list = myEleves ?? [];
    return ecoleId
      ? list.filter((e) => String(e.etablissementId) === String(ecoleId))
      : list;
  }, [myEleves, ecoleId]);

  // When child list arrives via URL (logged-in parent from dashboard)
  useEffect(() => {
    if (!eleveIdParam || !myEleves || elevePrefilled) return;
    const child = myEleves.find((e) => String(e.id) === String(eleveIdParam));
    if (!child) return;
    setSelectedEleve(myEleveToRow(child));
    setElevePrefilled(true);
  }, [eleveIdParam, myEleves, elevePrefilled]);

  // Soldes de l'élève sélectionné : pour un parent qui choisit un de ses
  // enfants, on désactive dans la liste les motifs déjà entièrement soldés.
  // L'`id` n'est présent que pour un enfant lié (myEleveToRow) — la recherche
  // libre invité renvoie aussi un id, mais l'endpoint est scopé au parent et
  // tombera en erreur silencieuse (motifs non désactivés) si non rattaché.
  const selectedEleveId = selectedEleve?.id ?? "";
  const { data: eleveSoldes } = useEleveSoldes(selectedEleveId, {
    enabled: isAuthenticated && !!selectedEleveId,
  });
  const soldedMotifIds = useMemo(
    () =>
      new Set(
        (eleveSoldes?.soldes ?? [])
          .filter((s) => s.solde)
          .map((s) => s.motifId),
      ),
    [eleveSoldes],
  );

  // Motifs FACULTATIFS (lot C — 29/07/2026) : dus seulement si activés pour
  // l'élève. Les soldes canoniques de l'enfant EXCLUENT les facultatifs non
  // activés → quand on les a (parent connecté + enfant lié), la liste payable
  // = exactement ses motifs dus. Sans soldes (invité), on masque tous les
  // facultatifs : impossible de vérifier l'activation, et le serveur
  // refuserait le paiement (409) — les facultatifs d'un élève se règlent
  // alors à la caisse ou via l'espace parent.
  const motifsPayables = useMemo(() => {
    const liste = motifs ?? [];
    if (eleveSoldes) {
      const dus = new Set(eleveSoldes.soldes.map((s) => s.motifId));
      return liste.filter((m) => m.obligatoire !== false || dus.has(m.id));
    }
    return liste.filter((m) => m.obligatoire !== false);
  }, [motifs, eleveSoldes]);

  function selectEleve(e: EleveSearchRow) {
    setSelectedEleve(e);
    setEleveQuery(`${e.nom} ${e.prenoms}`);
  }
  function selectMyChild(child: MyEleve) {
    setSelectedEleve(myEleveToRow(child));
  }
  function resetEleve() {
    setSelectedEleve(null);
    setEleveQuery("");
  }

  // Coordonnées payeur (obligatoires côté backend MVP)
  const [payer, setPayer] = useState({
    email: "",
    telephone: "",
    prenoms: "",
    nom: "",
  });
  useEffect(() => {
    if (isAuthenticated && user) {
      setPayer((p) => ({
        email: p.email || user.email || "",
        telephone: p.telephone || user.phone || "",
        prenoms: p.prenoms || user.prenoms || "",
        nom: p.nom || user.nom || "",
      }));
    }
  }, [isAuthenticated, user]);

  const selectedMotif = motifs?.find((m) => m.id === motifId);
  /** Tarif affiché du motif : celui de la classe de l'enfant s'il est connu, sinon le forfait. */
  const montantDuMotif = (m: Motif) => m.montantApplicableXof ?? m.montantXof;

  // Reste à régler (22/10/2026, décision du PO du 21/10) : une fois l'enfant
  // et le motif choisis, le serveur donne le reste du motif et celui de
  // chaque tranche non soldée. Le parent paie le reste exact d'une tranche ou
  // du motif ; le montant ne se saisit pas. Avant, /payer imposait le tarif
  // annuel complet, sans tenir compte du déjà payé, et sans tranche.
  const matriculeRetenu = (selectedEleve?.matricule || matricule).trim();
  const resteQuery = useQuery({
    queryKey: ["reste-a-regler", ecoleId, motifId, matriculeRetenu],
    queryFn: () => getResteARegler(ecoleId!, motifId!, matriculeRetenu),
    enabled: !!ecoleId && !!motifId && !!matriculeRetenu,
    retry: false,
  });
  const optionsReste = useMemo(
    () => (resteQuery.data ? optionsDeReste(resteQuery.data) : []),
    [resteQuery.data],
  );
  const [choixReste, setChoixReste] = useState<string | null>(null);
  const choix = optionChoisie(optionsReste, choixReste);
  const montantXof = choix?.montant ?? null;

  const [submitting, setSubmitting] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  const formState: PayerFormState = {
    ecoleId,
    motifId,
    montantXof,
    methode,
    eleveMode: selectedEleve ? "matricule" : "matricule",
    // Matricule effectif : celui de l'élève SÉLECTIONNÉ (recherche par nom) en
    // priorité, sinon celui SAISI. Sans ça, sélectionner un élève sans retaper
    // son matricule échouait la validation ("Matricule invalide") alors que le
    // matricule est connu — vrai pour le paiement en ligne ET la déclaration
    // hors système (#payer).
    matricule: selectedEleve?.matricule || matricule,
    eleve: {
      nom: selectedEleve?.nom ?? "",
      prenoms: selectedEleve?.prenoms ?? "",
      dateNaissance: selectedEleve?.dateNaissance ?? "",
    },
    payer,
  };
  // Le bouton reste cliquable (sauf pendant l'envoi) : cliquer sur un formulaire
  // incomplet affiche le 1er champ manquant + les erreurs inline (firstPayerError),
  // plutôt qu'un bouton grisé sans explication de ce qui manque (#70).

  async function handlePayer() {
    const problem = firstPayerError(formState);
    if (problem) {
      setShowErrors(true);
      toast.error(problem);
      return;
    }
    setSubmitting(true);
    try {
      const body: InitiatePaiementGuestBody = {
        ecoleId: ecoleId!,
        motifId: motifId!,
        montantXof: montantXof!,
        methode: methode!,
        payer,
      };
      // La tranche choisie part au serveur : il contrôle son reste exact et
      // le paiement comptabilisé la gardera.
      if (choix?.echeanceId) body.motifEcheanceId = choix.echeanceId;
      if (selectedEleve) {
        body.eleveMatricule = selectedEleve.matricule;
        /* v8 ignore start -- garde défensive : sans élève sélectionné, la
           validation (eleveMode "matricule") impose déjà un matricule valide,
           donc matricule.trim() est toujours vrai ici. La branche fausse est
           donc inatteignable. */
      } else if (matricule.trim()) {
        body.eleveMatricule = matricule.trim();
      }
      /* v8 ignore stop */
      const data = await initiatePaiementGuest(body);

      // Persist guest token so /paiement/:token can read it on return.
      if (data.guestToken && data.paiementId) {
        try {
          sessionStorage.setItem(
            `catho.guestToken.${data.paiementId}`,
            data.guestToken,
          );
        } catch {
          /* ignore */
        }
      }

      const token = data.guestToken ?? data.paiementId;
      if (data.redirectUrl) {
        window.location.href = data.redirectUrl;
      } else if (data.devSimulateUrl) {
        toast.info("Simulateur DEV : redirection…");
        window.location.href = data.devSimulateUrl;
      } else {
        setLocation(`/paiement/${token}`);
      }
    } catch (err: unknown) {
      if (err instanceof ApiError) {
        if (err.status === 404 && /élève|eleve|matricule/i.test(err.message)) toast.error("Matricule introuvable dans SEDEC. Vérifiez-le ou utilisez la saisie manuelle.");
        else if (err.status === 503 || err.status === 502) toast.error("Service de paiement momentanément indisponible. Réessayez dans un instant.");
        else if (err.status === 400 || err.status === 422 || err.status === 409)
          toast.error(friendlyError(err));
        else toast.error(friendlyError(err));
      } else {
        toast.error(friendlyError(err));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-2xl mx-auto px-6 py-12">
      <Eyebrow>Paiement sécurisé</Eyebrow>
      <h1 className="mt-1 mb-2 text-[28px] sm:text-[34px] leading-tight [font-family:'Cormorant_Garamond',serif] font-bold text-navy">
        Paiement
      </h1>
      {!isAuthenticated && (
        <p className="text-sm text-navy/60 mb-8">
          Aucun compte requis. Vous recevrez le reçu par email et SMS.
        </p>
      )}

      <div className="space-y-6">
        {/* Step: École */}
        <Card className="p-6 rounded-[18px] border-[#E5E5E5] shadow-[0_6px_16px_rgba(27,42,94,0.06)]">
          <h2 className="text-lg font-bold text-navy flex items-center gap-2 mb-4">
            <Building2 size={20} className="text-coral" /> École
          </h2>
          {ecole ? (
            <div className="bg-cream p-4 rounded-xl font-medium text-navy">
              {ecole.denomination}
            </div>
          ) : (
            <div className="text-sm text-navy/60">
              Veuillez sélectionner une école depuis l'annuaire ou le tableau de
              bord.
            </div>
          )}
          <a
            href="/dashboard"
            className="inline-block mt-3 text-xs font-bold text-[#B8881F] hover:underline"
          >
            ← Retour au tableau de bord
          </a>
        </Card>

        {/* Step: Enfant — recherche par nom ou matricule */}
        <Card className="p-6 rounded-[18px] border-[#E5E5E5] shadow-[0_6px_16px_rgba(27,42,94,0.06)]">
          <h2 className="text-lg font-bold text-navy flex items-center gap-2 mb-4">
            <User size={20} className="text-sage" /> Enfant
          </h2>

          {selectedEleve ? (
            <div className="flex items-center justify-between gap-3 rounded-[14px] border border-[#3F8F5C]/30 bg-[#E4F1E8]/50 px-4 py-3">
              <div className="flex min-w-0 items-center gap-3">
                <UserCheck size={22} className="shrink-0 text-[#3F8F5C]" />
                <div className="min-w-0">
                  <p className="truncate font-bold text-navy">
                    {selectedEleve.nom} {selectedEleve.prenoms}
                  </p>
                  <p className="text-xs text-navy/60">
                    {selectedEleve.classe ? `${selectedEleve.classe} · ` : ""}
                    {selectedEleve.matricule}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={resetEleve}
                className="shrink-0 text-xs font-bold text-[#B8881F] hover:underline"
              >
                Changer
              </button>
            </div>
          ) : isAuthenticated ? (
            // Parent connecté : on propose UNIQUEMENT ses enfants liés (pas de
            // recherche libre/globale). Filtré par l'école ciblée le cas échéant.
            <div data-testid="my-children-picker">
              <Label className="text-xs text-navy/70">
                Sélectionnez votre enfant
              </Label>
              {myChildrenForEcole.length > 0 ? (
                <div className="mt-1 grid gap-2">
                  {myChildrenForEcole.map((child) => (
                    <button
                      key={child.id}
                      type="button"
                      onClick={() => selectMyChild(child)}
                      className="flex w-full items-center gap-3 rounded-xl border border-navy/15 bg-white px-4 py-3 text-left transition-colors hover:bg-cream"
                    >
                      <GraduationCap size={18} className="shrink-0 text-navy/50" />
                      <div className="min-w-0">
                        <span className="block truncate text-sm font-medium text-navy">
                          {child.nom} {child.prenoms}
                        </span>
                        <span className="block truncate text-xs text-navy/55">
                          {child.classeLibelle ? `${child.classeLibelle} · ` : ""}
                          {child.matricule ?? ""}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="mt-2 text-sm text-navy/60">
                  Aucun enfant lié à votre compte pour cette école.{" "}
                  <a href="/dashboard" className="text-[#B8881F] underline">
                    Ajouter un enfant
                  </a>
                </p>
              )}
            </div>
          ) : (
            <div className="relative">
              <Label htmlFor="eleve-search" className="text-xs text-navy/70">
                Matricule de votre enfant
              </Label>
              <p className="mt-0.5 text-[11px] text-navy/55">
                Tapez le matricule exact : nous affichons le prénom et la classe pour confirmer.
              </p>
              <div className="relative mt-1">
                <Search
                  size={16}
                  className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-navy/40"
                />
                <Input
                  id="eleve-search"
                  className="pl-9"
                  placeholder="ex: SEDEC-2026-00123"
                  value={eleveQuery}
                  onChange={(e) => setEleveQuery(e.target.value)}
                  autoComplete="off"
                />
                {searching && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="w-4 h-4 border-2 border-navy/30 border-t-[#E6B547] rounded-full animate-spin" />
                  </div>
                )}
              </div>
              {eleveQuery.trim().length >= 2 && !searching && !selectedEleve && (
                <div className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-xl border border-navy/15 bg-white shadow-lg">
                  {eleveResults && eleveResults.length > 0 ? (
                    eleveResults.map((e) => (
                      <button
                        key={e.matricule}
                        type="button"
                        onClick={() => selectEleve(e)}
                        className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-cream transition-colors"
                      >
                        <GraduationCap size={18} className="shrink-0 text-navy/50" />
                        <div className="min-w-0 text-left">
                          <span className="block truncate text-sm font-medium text-navy">
                            {e.nom} {e.prenoms}
                          </span>
                          <span className="block truncate text-xs text-navy/55">
                            {e.classe ? `${e.classe} · ` : ""}{e.matricule}
                          </span>
                        </div>
                      </button>
                    ))
                  ) : tropDeRecherches ? (
                    <div role="status" className="px-4 py-3 text-sm text-amber-800">
                      Trop de recherches depuis cette connexion. Réessayez dans quelques minutes.
                    </div>
                  ) : (
                    <div className="px-4 py-3 text-sm text-navy/60">
                      Aucun élève pour ce matricule cette année.{" "}
                      <button
                        type="button"
                        className="text-[#B8881F] underline"
                        onClick={() => {
                          setMatricule(eleveQuery.trim());
                          setEleveQuery("");
                        }}
                      >
                        Utiliser comme matricule
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {!selectedEleve && matricule.trim() && (
            <div className="mt-3 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
              <Hash size={14} />
              <span className="font-mono">{matricule}</span>
              <button type="button" onClick={() => setMatricule("")} className="ml-auto text-xs font-bold text-amber-600 hover:underline">Retirer</button>
            </div>
          )}
        </Card>

        {/* Step: Motif */}
        <Card className="p-6 rounded-[18px] border-[#E5E5E5] shadow-[0_6px_16px_rgba(27,42,94,0.06)]">
          <h2 className="text-lg font-bold text-navy flex items-center gap-2 mb-4">
            <CreditCard size={20} className="text-gold" /> Motif
          </h2>
          <div className="grid gap-3">
            {motifsPayables.map((m) => {
              const isSolde = soldedMotifIds.has(m.id);
              return (
                <button
                  key={m.id}
                  type="button"
                  disabled={isSolde}
                  aria-disabled={isSolde}
                  data-testid={`motif-${m.id}`}
                  onClick={() => setMotifId(m.id)}
                  className={`p-4 rounded-[14px] border text-left transition-colors flex justify-between items-center ${
                    isSolde
                      ? "border-green-100 bg-green-50/60 cursor-not-allowed opacity-70"
                      : motifId === m.id
                        ? "border-navy ring-1 ring-navy bg-navy/5 cursor-pointer"
                        : "border-[#E5E5E5] bg-white hover:border-navy/30 cursor-pointer"
                  }`}
                >
                  <div>
                    <div className="font-bold text-navy">{m.libelle}</div>
                    <div className="text-xs text-navy/60">
                      {isSolde ? "déjà soldé" : m.categorie}
                    </div>
                  </div>
                  {montantDuMotif(m) != null ? (
                    <div className="[font-family:'Cormorant_Garamond',serif] font-bold text-[22px] leading-none text-navy">
                      {formatXof(montantDuMotif(m))}
                    </div>
                  ) : (
                    // Motif tarifé par classe (barème) : pas de montant unique
                    // tant que l'enfant n'est pas choisi. Jamais « 0 F CFA ».
                    <div className="text-sm font-bold text-navy/60 text-right">
                      Selon la classe
                    </div>
                  )}
                </button>
              );
            })}
            {motifsPayables.length === 0 && (
              <div className="text-sm text-navy/60">
                Aucun motif publié pour cette école pour le moment. Contactez
                l'établissement.
              </div>
            )}
          </div>
        </Card>

        {/* Step: Montant (tranches non soldées et reste du motif) */}
        {selectedMotif && (
          <Card
            data-testid="reste-a-regler"
            className="p-6 rounded-[18px] border-[#E5E5E5] shadow-[0_6px_16px_rgba(27,42,94,0.06)]"
          >
            <h2 className="text-lg font-bold text-navy flex items-center gap-2 mb-4">
              <CalendarClock size={20} className="text-gold" /> Ce que vous réglez
            </h2>
            <ResteAReglerPicker
              matricule={matriculeRetenu}
              reste={resteQuery.data}
              error={resteQuery.error}
              choix={choix}
              onChoisir={(o) => setChoixReste(cleOption(o))}
              onReessayer={() => void resteQuery.refetch()}
            />
          </Card>
        )}

        {/* Step: Coordonnées payeur */}
        <Card className="p-6 rounded-[18px] border-[#E5E5E5] shadow-[0_6px_16px_rgba(27,42,94,0.06)]">
          <h2 className="text-lg font-bold text-navy flex items-center gap-2 mb-4">
            <UserCircle2 size={20} className="text-coral" /> Vos coordonnées
          </h2>
          <p className="text-xs text-navy/60 mb-4">
            Pour vous envoyer le reçu officiel et pouvoir traiter toute
            réclamation.
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="payer-prenoms" className="text-xs text-navy/70">
                Prénoms *
              </Label>
              <Input
                id="payer-prenoms"
                value={payer.prenoms}
                onChange={(e) =>
                  setPayer((p) => ({ ...p, prenoms: e.target.value }))
                }
              />
            </div>
            <div>
              <Label htmlFor="payer-nom" className="text-xs text-navy/70">
                Nom *
              </Label>
              <Input
                id="payer-nom"
                value={payer.nom}
                onChange={(e) =>
                  setPayer((p) => ({ ...p, nom: e.target.value }))
                }
              />
            </div>
            <div>
              <Label htmlFor="payer-email" className="text-xs text-navy/70">
                Email *
              </Label>
              <Input
                id="payer-email"
                type="email"
                value={payer.email}
                onChange={(e) =>
                  setPayer((p) => ({ ...p, email: e.target.value }))
                }
                placeholder="vous@example.com"
                aria-invalid={showErrors && !isValidEmail(payer.email)}
              />
              {showErrors && !isValidEmail(payer.email) && (
                <p className="text-xs text-red-500 mt-1">
                  Adresse email invalide.
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="payer-tel" className="text-xs text-navy/70">
                Téléphone *
              </Label>
              <Input
                id="payer-tel"
                value={payer.telephone}
                onChange={(e) =>
                  setPayer((p) => ({ ...p, telephone: e.target.value }))
                }
                placeholder="+225 0X XX XX XX XX"
                aria-invalid={showErrors && !isValidPhone(payer.telephone)}
              />
              {showErrors && !isValidPhone(payer.telephone) && (
                <p className="text-xs text-red-500 mt-1">
                  Numéro de téléphone invalide.
                </p>
              )}
            </div>
          </div>
        </Card>

        {/* Step: Méthode — onglet en ligne (Mobile Money) / hors système */}
        <Card className="p-6 rounded-[18px] border-[#E5E5E5] shadow-[0_6px_16px_rgba(27,42,94,0.06)]">
          <h2 className="text-lg font-bold text-navy mb-3">Mode de paiement</h2>

          <div
            role="tablist"
            aria-label="Type de règlement"
            className="grid grid-cols-2 gap-1 rounded-xl bg-cream p-1 mb-4"
          >
            <button
              type="button"
              role="tab"
              aria-selected={paiementMode === "online"}
              onClick={() => setPaiementMode("online")}
              data-testid="tab-online"
              className={`flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-bold transition-colors ${
                paiementMode === "online"
                  ? "bg-white text-navy shadow-sm"
                  : "text-navy/50 hover:text-navy/70"
              }`}
            >
              <Smartphone size={15} /> Payer en ligne
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={paiementMode === "offline"}
              onClick={() => setPaiementMode("offline")}
              data-testid="tab-offline"
              className={`flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-bold transition-colors ${
                paiementMode === "offline"
                  ? "bg-white text-navy shadow-sm"
                  : "text-navy/50 hover:text-navy/70"
              }`}
            >
              <Banknote size={15} /> J'ai déjà payé
            </button>
          </div>

          {paiementMode === "offline" ? (
            <HorsSystemeForm
              formState={formState}
              eleveMatricule={selectedEleve?.matricule || matricule.trim()}
              motifEcheanceId={choix?.echeanceId ?? null}
            />
          ) : (
            <>
          <p className="text-xs text-navy/60 mb-4">
            Choisissez votre opérateur Mobile Money. Le paiement est sécurisé.
          </p>
          <div
            role="radiogroup"
            aria-label="Mode de paiement"
            className="grid sm:grid-cols-2 gap-3"
          >
            {METHODES.map((m) => {
              const meta = OPERATEUR_META[m];
              const isSelected = methode === m;
              return (
                <button
                  key={m}
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  onClick={() => setMethode(m)}
                  className={`text-left bg-white rounded-[18px] p-4 border transition-all hover:-translate-y-px hover:shadow-[0_6px_16px_rgba(27,42,94,0.10)] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[#E6B547]/35 ${
                    isSelected
                      ? "border-navy ring-1 ring-navy shadow-[0_6px_16px_rgba(27,42,94,0.12)]"
                      : "border-[#E5E5E5] shadow-[0_6px_16px_rgba(27,42,94,0.06)] hover:border-navy/40"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <OperateurLogo op={m} size={44} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-bold text-sm text-navy truncate">
                          {meta.name}
                        </p>
                        {isSelected && (
                          <span
                            aria-hidden
                            className="w-5 h-5 rounded-full bg-navy flex items-center justify-center text-white shrink-0"
                          >
                            <Check size={12} />
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-navy/60 mt-0.5">
                        {meta.description}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {methode && (
            <div className="mt-4 rounded-xl border border-orange-50 bg-cream p-4 flex items-start gap-3">
              <OperateurLogo op={methode} size={32} />
              <p className="text-xs text-navy/70 leading-relaxed">
                {`Vous recevrez une demande de validation ${OPERATEUR_META[methode].name} sur votre téléphone après confirmation.`}
              </p>
            </div>
          )}
            </>
          )}
        </Card>

        {paiementMode === "online" && (
          <Button
            onClick={handlePayer}
            disabled={submitting}
            className="w-full bg-navy hover:bg-[#13204A] text-white h-[56px] rounded-[14px] font-semibold text-[16px] shadow-[0_10px_18px_rgba(19,32,74,0.22)] transition-all hover:-translate-y-px flex items-center justify-center gap-2 focus-visible:ring-[3px] focus-visible:ring-[#E6B547]/35"
          >
            {submitting ? "Traitement…" : montantXof != null ? `Payer ${formatXof(montantXof)}` : "Payer"}{" "}
            <ArrowRight size={20} />
          </Button>
        )}
      </div>
    </div>
  );
}
